/**
 * Persistent generators for surface-created cutter objects.
 *
 * Surface Box/Cylinder/Poly are drawn interactively in the viewport, but once
 * confirmed they become ordinary generator-backed SceneObjects. The captured
 * footprint/depth is stored as hidden generator params; user-facing params
 * (Hollow, Thickness, Cap Scale, Array) remain editable indefinitely and
 * round-trip through the normal generator codec.
 */

import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import { offsetSimplePolygonInward } from "../coplanarBoundaryLoop.ts";
import { ARRAY_MODIFIER_MAX_COUNT } from "../modifierLimits.ts";
import type { PrimitiveGeometry } from "../primitives.ts";
import {
  clampSurfaceCutStartOffset,
  surfaceCutStartOffset,
  surfaceCutStartOffsetRange as surfaceCutStartOffsetDepthRange,
} from "../surfaceCutOffset.ts";
import {
  buildSurfaceCutterMesh,
  clampSurfaceHollowThickness,
  type SurfaceCutterSpec,
  surfaceCapScaleFromParams,
  surfaceCutterFootprintMin,
} from "../surfaceHollowCutter.ts";
import { buildSurfaceArrayModifier } from "../surfaceOperatorModifiers.ts";
import {
  normalizeSurfaceProfile,
  type SurfaceCutterProfile,
} from "../surfaceProfiles.ts";
import type {
  Generator,
  GeneratorListItem,
  GeneratorListItemType,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
  GeneratorRef,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  intParam,
  listParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

export const SURFACE_BOX_GENERATOR_ID = "surface_box";
export const SURFACE_CYLINDER_GENERATOR_ID = "surface_cylinder";
export const SURFACE_POLY_GENERATOR_ID = "surface_poly";
export const SURFACE_INSET_GENERATOR_ID = "surface_inset";

export type SurfaceGeneratorId =
  | typeof SURFACE_BOX_GENERATOR_ID
  | typeof SURFACE_CYLINDER_GENERATOR_ID
  | typeof SURFACE_POLY_GENERATOR_ID
  | typeof SURFACE_INSET_GENERATOR_ID;

const IDENTITY_SCALE: [number, number, number] = [1, 1, 1];
const HOLLOW_THICKNESS_DEFAULT_RATIO = 0.1;
const HOLLOW_THICKNESS_MAX_RATIO = 0.45;
const HOLLOW_THICKNESS_STEP_RATIO = 0.005;

type SurfaceEditableParams = Pick<
  SurfaceGeneratorParams,
  | "capScale"
  | "count"
  | "cutStartOffset"
  | "hollow"
  | "hollowThickness"
  | "loopCuts"
  | "loopProfile"
  | "offsetX"
  | "offsetY"
  | "offsetZ"
>;

interface SurfaceGeneratorParams extends GeneratorParams {
  capScale: number;
  count: number;
  cutStartOffset: number;
  hollow: boolean;
  hollowThickness: number;
  loopCuts: number;
  loopProfile: number;
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  profile: string;
  rampAxisX: number;
  rampAxisY: number;
  rampBase: number;
  rampLead: number;
}

interface SurfaceBoxParams extends SurfaceGeneratorParams {
  width: number;
  depth: number;
  height: number;
  reverse: boolean;
}

interface SurfaceCylinderParams extends SurfaceGeneratorParams {
  radiusX: number;
  radiusY: number;
  height: number;
  segments: number;
  reverse: boolean;
}

interface SurfacePolyParams extends SurfaceGeneratorParams {
  height: number;
  reverse: boolean;
  outerLoop: readonly GeneratorListItem[];
}

interface SurfaceInsetParams extends SurfaceGeneratorParams {
  height: number;
  reverse: boolean;
  insetDistance: number;
  boundaryLoop: readonly GeneratorListItem[];
}

const SURFACE_COMMON_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "capScale",
    label: "Cap Scale",
    type: "number",
    default: 1,
    min: 0,
    max: 2,
    step: 0.01,
  },
  {
    id: "loopCuts",
    label: "Loop Cuts",
    type: "number",
    unit: "count",
    default: 0,
    min: 0,
    max: 16,
    step: 1,
  },
  {
    id: "loopProfile",
    label: "Profile",
    type: "number",
    default: 0.5,
    min: 0,
    max: 1,
    step: 0.05,
    showIf: [
      { id: "loopCuts", value: 0, negate: true },
      { id: "capScale", value: 1, negate: true },
    ],
  },
  {
    id: "cutStartOffset",
    label: "Cut Offset",
    type: "number",
    unit: "meters",
    default: surfaceCutStartOffset(1),
    min: surfaceCutStartOffset(1),
    max: 0.05,
    step: 0.001,
    resolveRange: surfaceCutStartOffsetGeneratorRange,
    showIf: { id: "reverse", value: true },
  },
  {
    id: "hollow",
    label: "Hollow",
    type: "boolean",
    default: false,
  },
  {
    id: "hollowThickness",
    label: "Thickness",
    type: "number",
    unit: "meters",
    default: 0.1,
    min: 0.001,
    max: 10,
    step: 0.005,
    resolveRange: surfaceHollowThicknessRange,
    showIf: { id: "hollow", value: true },
  },
  {
    type: "section",
    id: "surface-array",
    label: "Array",
    defaultCollapsed: true,
    params: [
      {
        id: "count",
        label: "Count",
        type: "number",
        unit: "count",
        default: 1,
        min: 1,
        max: ARRAY_MODIFIER_MAX_COUNT,
        step: 1,
      },
      {
        id: "offsetX",
        label: "X Offset",
        type: "number",
        default: 1,
        min: -4,
        max: 4,
        step: 0.05,
        showIf: { id: "count", value: 1, negate: true },
      },
      {
        id: "offsetY",
        label: "Y Offset",
        type: "number",
        default: 0,
        min: -4,
        max: 4,
        step: 0.05,
        showIf: { id: "count", value: 1, negate: true },
      },
      {
        id: "offsetZ",
        label: "Z Offset",
        type: "number",
        default: 0,
        min: -4,
        max: 4,
        step: 0.05,
        showIf: { id: "count", value: 1, negate: true },
      },
    ],
  },
];

const SURFACE_PROFILE_SOURCE_PARAMS: readonly GeneratorParamSpec[] = [
  hiddenEnum("profile", "prism", [
    { value: "prism", label: "Prism" },
    { value: "linearRamp", label: "Linear Ramp" },
  ]),
  hiddenNumber("rampAxisX", 1),
  hiddenNumber("rampAxisY", 0),
  hiddenNumber("rampBase", 0),
  hiddenNumber("rampLead", 1),
];

const SURFACE_BOX_SOURCE_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "surface-box-source",
    label: "Source",
    hidden: true,
    params: [
      hiddenNumber("width", 1),
      hiddenNumber("depth", 1),
      hiddenNumber("height", 1),
      hiddenBoolean("reverse", false),
      ...SURFACE_PROFILE_SOURCE_PARAMS,
    ],
  },
];

const SURFACE_CYLINDER_SOURCE_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "surface-cylinder-source",
    label: "Source",
    hidden: true,
    params: [
      hiddenNumber("radiusX", 0.5),
      hiddenNumber("radiusY", 0.5),
      hiddenNumber("height", 1),
      hiddenNumber("segments", 32),
      hiddenBoolean("reverse", false),
      ...SURFACE_PROFILE_SOURCE_PARAMS,
    ],
  },
];

const POLY_POINT_ITEM: GeneratorListItemType = {
  id: "point",
  label: "Point",
  paramSpecs: [hiddenNumber("x", 0), hiddenNumber("y", 0)],
  defaultParams: { x: 0, y: 0 },
};

const SURFACE_POLY_SOURCE_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "surface-poly-source",
    label: "Source",
    hidden: true,
    params: [
      hiddenNumber("height", 1),
      hiddenBoolean("reverse", false),
      ...SURFACE_PROFILE_SOURCE_PARAMS,
      {
        id: "outerLoop",
        label: "Outline",
        type: "list",
        hidden: true,
        itemTypes: [POLY_POINT_ITEM],
        default: [
          pointItem("p0", -0.5, -0.5),
          pointItem("p1", 0.5, -0.5),
          pointItem("p2", 0.5, 0.5),
          pointItem("p3", -0.5, 0.5),
        ],
      },
    ],
  },
];

const SURFACE_INSET_VISIBLE_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "insetDistance",
    label: "Inset",
    type: "number",
    unit: "meters",
    default: 0.1,
    min: 0,
    max: 10,
    step: 0.005,
    resolveRange: surfaceInsetDistanceRange,
  },
];

const SURFACE_INSET_SOURCE_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "surface-inset-source",
    label: "Source",
    hidden: true,
    params: [
      hiddenNumber("height", 1),
      hiddenBoolean("reverse", false),
      ...SURFACE_PROFILE_SOURCE_PARAMS,
      {
        id: "boundaryLoop",
        label: "Boundary",
        type: "list",
        hidden: true,
        itemTypes: [POLY_POINT_ITEM],
        default: [
          pointItem("p0", -0.5, -0.5),
          pointItem("p1", 0.5, -0.5),
          pointItem("p2", 0.5, 0.5),
          pointItem("p3", -0.5, 0.5),
        ],
      },
    ],
  },
];

const SURFACE_BOX_SPECS: readonly GeneratorParamSpec[] = [
  ...SURFACE_COMMON_SPECS,
  ...SURFACE_BOX_SOURCE_SPECS,
];
const SURFACE_CYLINDER_SPECS: readonly GeneratorParamSpec[] = [
  ...SURFACE_COMMON_SPECS,
  ...SURFACE_CYLINDER_SOURCE_SPECS,
];
const SURFACE_POLY_SPECS: readonly GeneratorParamSpec[] = [
  ...SURFACE_COMMON_SPECS,
  ...SURFACE_POLY_SOURCE_SPECS,
];
const SURFACE_INSET_SPECS: readonly GeneratorParamSpec[] = [
  ...SURFACE_INSET_VISIBLE_SPECS,
  ...SURFACE_COMMON_SPECS,
  ...SURFACE_INSET_SOURCE_SPECS,
];

export const SURFACE_BOX_GENERATOR: Generator =
  defineGenerator<SurfaceBoxParams>({
    id: SURFACE_BOX_GENERATOR_ID,
    label: "Surface Box",
    category: "primitive",
    paramSpecs: SURFACE_BOX_SPECS,
    defaultParams: paramsFromSpecs(SURFACE_BOX_SPECS) as SurfaceBoxParams,
    build: (p) => surfaceOutput(SURFACE_BOX_GENERATOR_ID, p),
    geometrySignature: (p) =>
      surfaceGeometrySignature(SURFACE_BOX_GENERATOR_ID, p),
  });

export const SURFACE_CYLINDER_GENERATOR: Generator =
  defineGenerator<SurfaceCylinderParams>({
    id: SURFACE_CYLINDER_GENERATOR_ID,
    label: "Surface Cylinder",
    category: "primitive",
    paramSpecs: SURFACE_CYLINDER_SPECS,
    defaultParams: paramsFromSpecs(
      SURFACE_CYLINDER_SPECS,
    ) as SurfaceCylinderParams,
    build: (p) => surfaceOutput(SURFACE_CYLINDER_GENERATOR_ID, p),
    geometrySignature: (p) =>
      surfaceGeometrySignature(SURFACE_CYLINDER_GENERATOR_ID, p),
  });

export const SURFACE_POLY_GENERATOR: Generator =
  defineGenerator<SurfacePolyParams>({
    id: SURFACE_POLY_GENERATOR_ID,
    label: "Surface Poly",
    category: "primitive",
    paramSpecs: SURFACE_POLY_SPECS,
    defaultParams: paramsFromSpecs(SURFACE_POLY_SPECS) as SurfacePolyParams,
    build: (p) => surfaceOutput(SURFACE_POLY_GENERATOR_ID, p),
    geometrySignature: (p) =>
      surfaceGeometrySignature(SURFACE_POLY_GENERATOR_ID, p),
  });

export const SURFACE_INSET_GENERATOR: Generator =
  defineGenerator<SurfaceInsetParams>({
    id: SURFACE_INSET_GENERATOR_ID,
    label: "Surface Inset",
    category: "primitive",
    paramSpecs: SURFACE_INSET_SPECS,
    defaultParams: paramsFromSpecs(SURFACE_INSET_SPECS) as SurfaceInsetParams,
    build: (p) => surfaceOutput(SURFACE_INSET_GENERATOR_ID, p),
    geometrySignature: (p) =>
      surfaceGeometrySignature(SURFACE_INSET_GENERATOR_ID, p),
  });

export interface SurfaceGeneratorMesh {
  geometry: PrimitiveGeometry;
  generatorModifiers: readonly BlendModifier[];
}

export function surfaceBoxParamsFromSpec(
  spec: Extract<SurfaceCutterSpec, { kind: "box" }>,
  controls: Partial<SurfaceEditableParams> = {},
): SurfaceBoxParams {
  return {
    ...(SURFACE_BOX_GENERATOR.defaultParams as SurfaceBoxParams),
    width: positive(spec.width, 1),
    depth: positive(spec.depth, 1),
    height: positive(spec.height, 1),
    reverse: spec.reverse,
    cutStartOffset: surfaceCutStartOffsetFromSpec(spec),
    ...surfaceProfileParamsFromSpec(spec.profile),
    hollowThickness: defaultSurfaceHollowThickness(spec),
    ...controls,
  };
}

export function surfaceCylinderParamsFromSpec(
  spec: Extract<SurfaceCutterSpec, { kind: "cylinder" }>,
  controls: Partial<SurfaceEditableParams> = {},
): SurfaceCylinderParams {
  return {
    ...(SURFACE_CYLINDER_GENERATOR.defaultParams as SurfaceCylinderParams),
    radiusX: positive(spec.radiusX, 0.5),
    radiusY: positive(spec.radiusY, 0.5),
    height: positive(spec.height, 1),
    segments: Math.max(3, Math.round(spec.segments)),
    reverse: spec.reverse,
    cutStartOffset: surfaceCutStartOffsetFromSpec(spec),
    ...surfaceProfileParamsFromSpec(spec.profile),
    hollowThickness: defaultSurfaceHollowThickness(spec),
    ...controls,
  };
}

export function surfacePolyParamsFromSpec(
  spec: Extract<SurfaceCutterSpec, { kind: "poly" }>,
  controls: Partial<SurfaceEditableParams> = {},
): SurfacePolyParams {
  return {
    ...(SURFACE_POLY_GENERATOR.defaultParams as SurfacePolyParams),
    height: positive(spec.height, 1),
    reverse: spec.reverse,
    cutStartOffset: surfaceCutStartOffsetFromSpec(spec),
    outerLoop: spec.outerLoop.map(([x, y], index) =>
      pointItem(`p${index}`, x, y),
    ),
    ...surfaceProfileParamsFromSpec(spec.profile),
    hollowThickness: defaultSurfaceHollowThickness(spec),
    ...controls,
  };
}

export function surfaceGeneratorRef(
  generatorId: SurfaceGeneratorId,
  params: GeneratorParams,
): GeneratorRef {
  return {
    generatorId,
    params,
    worldSpace: false,
    geometrySignature: surfaceGeometrySignature(generatorId, params),
  };
}

export function buildSurfaceGeneratorMesh(
  generatorId: SurfaceGeneratorId,
  params: GeneratorParams,
): SurfaceGeneratorMesh {
  const spec = surfaceSpecFromParams(generatorId, params);
  const mesh = buildSurfaceCutterMesh(spec, {
    hollow: boolParam(params, "hollow", false),
    hollowThickness: surfaceHollowThicknessFromParams(spec, params),
    capScale: surfaceCapScaleFromParams(params),
    loopCuts: surfaceLoopCutsFromParams(params),
    loopProfile: surfaceLoopProfileFromParams(params),
  });
  const baked = bakeMeshScale(mesh.geometry, mesh.scale);
  const anchorShift = surfaceCutOffsetCompensationShift(spec);
  const vertices =
    Math.abs(anchorShift) <= 1e-8
      ? baked.vertices
      : translateVerticesZ(baked.vertices, anchorShift);
  const arrayModifier = buildSurfaceArrayModifier(params);
  return {
    geometry:
      vertices === baked.vertices
        ? baked
        : {
            ...baked,
            vertices,
          },
    generatorModifiers: arrayModifier ? [arrayModifier] : [],
  };
}

function surfaceOutput(
  generatorId: SurfaceGeneratorId,
  params: GeneratorParams,
): GeneratorOutput {
  const result = buildSurfaceGeneratorMesh(generatorId, params);
  return {
    kind: "mesh",
    geometry: result.geometry,
    generatorModifiers: result.generatorModifiers,
  };
}

function surfaceGeometrySignature(
  generatorId: SurfaceGeneratorId,
  params: GeneratorParams,
): string {
  const spec = surfaceSpecFromParams(generatorId, params);
  return JSON.stringify({
    generatorId,
    spec,
    hollow: boolParam(params, "hollow", false),
    hollowThickness: surfaceHollowThicknessFromParams(spec, params),
    capScale: surfaceCapScaleFromParams(params),
    loopCuts: surfaceLoopCutsFromParams(params),
    loopProfile: surfaceLoopProfileFromParams(params),
  });
}

function surfaceHollowThicknessRange(params: GeneratorParams): {
  default: number;
  min: number;
  max: number;
  step: number;
} {
  const footprintMin = surfaceFootprintMinFromParams(params);
  if (!(footprintMin > 0)) {
    return {
      default: 0.1,
      min: 0.001,
      max: 10,
      step: 0.005,
    };
  }
  const max = clampSurfaceHollowThickness(
    footprintMin * HOLLOW_THICKNESS_MAX_RATIO,
    footprintMin,
  );
  const min = Math.min(max, Math.max(1e-6, footprintMin * 0.001));
  return {
    default: defaultSurfaceHollowThicknessFromFootprint(footprintMin),
    min,
    max,
    step: Math.max(1e-6, footprintMin * HOLLOW_THICKNESS_STEP_RATIO),
  };
}

function surfaceCutStartOffsetGeneratorRange(params: GeneratorParams): {
  default: number;
  min: number;
  max: number;
  step: number;
} {
  return surfaceCutStartOffsetDepthRange(numParam(params, "height", 1));
}

function surfaceCutStartOffsetFromParams(
  height: number,
  reverse: boolean,
  params: GeneratorParams,
): number {
  if (!reverse) return 0;
  return clampSurfaceCutStartOffset(
    numParam(params, "cutStartOffset", surfaceCutStartOffset(height)),
    height,
  );
}

function surfaceCutStartOffsetFromSpec(spec: SurfaceCutterSpec): number {
  return spec.reverse
    ? clampSurfaceCutStartOffset(spec.cutStartOffset, spec.height)
    : 0;
}

function surfaceHollowThicknessFromParams(
  spec: SurfaceCutterSpec,
  params: GeneratorParams,
): number {
  const footprintMin = surfaceCutterFootprintMin(spec);
  return clampSurfaceHollowThickness(
    numParam(params, "hollowThickness", defaultSurfaceHollowThickness(spec)),
    footprintMin,
  );
}

function surfaceLoopCutsFromParams(params: GeneratorParams): number {
  return Math.max(0, Math.min(16, intParam(params, "loopCuts", 0)));
}

function surfaceLoopProfileFromParams(params: GeneratorParams): number {
  const value = numParam(params, "loopProfile", 0.5);
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0.5;
}

function defaultSurfaceHollowThickness(spec: SurfaceCutterSpec): number {
  return defaultSurfaceHollowThicknessFromFootprint(
    surfaceCutterFootprintMin(spec),
  );
}

function defaultSurfaceHollowThicknessFromFootprint(
  footprintMin: number,
): number {
  return clampSurfaceHollowThickness(
    footprintMin * HOLLOW_THICKNESS_DEFAULT_RATIO,
    footprintMin,
  );
}

function surfaceFootprintMinFromParams(params: GeneratorParams): number {
  const width = params.width;
  const depth = params.depth;
  if (typeof width === "number" || typeof depth === "number") {
    return surfaceCutterFootprintMin({
      kind: "box",
      width: positive(numParam(params, "width", 1), 1),
      depth: positive(numParam(params, "depth", 1), 1),
      height: 1,
      reverse: false,
      cutStartOffset: 0,
    });
  }

  const radiusX = params.radiusX;
  const radiusY = params.radiusY;
  if (typeof radiusX === "number" || typeof radiusY === "number") {
    return surfaceCutterFootprintMin({
      kind: "cylinder",
      radiusX: positive(numParam(params, "radiusX", 0.5), 0.5),
      radiusY: positive(numParam(params, "radiusY", 0.5), 0.5),
      height: 1,
      segments: 32,
      reverse: false,
      cutStartOffset: 0,
    });
  }

  // Inset generator stores its source loop under `boundaryLoop`; the offset
  // version becomes the runtime cutter loop after `offsetSimplePolygonInward`.
  // The min-extent that drives hollow-thickness ranges is taken on that
  // post-offset loop so thickness limits match the actual cutter geometry.
  if (Array.isArray(params.boundaryLoop)) {
    const boundary = boundaryLoopFromParams(params);
    const insetDistance = Math.max(0, numParam(params, "insetDistance", 0));
    const offset = offsetSimplePolygonInward(boundary, insetDistance);
    const loop =
      offset.loop.length >= 3
        ? (offset.loop as readonly (readonly [number, number])[])
        : boundary;
    return surfaceCutterFootprintMin({
      kind: "poly",
      outerLoop: loop,
      height: 1,
      reverse: false,
      cutStartOffset: 0,
    });
  }

  return surfaceCutterFootprintMin({
    kind: "poly",
    outerLoop: outerLoopFromParams(params),
    height: 1,
    reverse: false,
    cutStartOffset: 0,
  });
}

function surfaceSpecFromParams(
  generatorId: SurfaceGeneratorId,
  params: GeneratorParams,
): SurfaceCutterSpec {
  switch (generatorId) {
    case SURFACE_BOX_GENERATOR_ID: {
      const height = positive(numParam(params, "height", 1), 1);
      const reverse = boolParam(params, "reverse", false);
      return {
        kind: "box",
        width: positive(numParam(params, "width", 1), 1),
        depth: positive(numParam(params, "depth", 1), 1),
        height,
        reverse,
        cutStartOffset: surfaceCutStartOffsetFromParams(
          height,
          reverse,
          params,
        ),
        profile: surfaceProfileFromParams(params),
      };
    }
    case SURFACE_CYLINDER_GENERATOR_ID: {
      const height = positive(numParam(params, "height", 1), 1);
      const reverse = boolParam(params, "reverse", false);
      return {
        kind: "cylinder",
        radiusX: positive(numParam(params, "radiusX", 0.5), 0.5),
        radiusY: positive(numParam(params, "radiusY", 0.5), 0.5),
        height,
        segments: Math.max(3, intParam(params, "segments", 32)),
        reverse,
        cutStartOffset: surfaceCutStartOffsetFromParams(
          height,
          reverse,
          params,
        ),
        profile: surfaceProfileFromParams(params),
      };
    }
    case SURFACE_POLY_GENERATOR_ID: {
      const height = positive(numParam(params, "height", 1), 1);
      const reverse = boolParam(params, "reverse", false);
      return {
        kind: "poly",
        outerLoop: outerLoopFromParams(params),
        height,
        reverse,
        cutStartOffset: surfaceCutStartOffsetFromParams(
          height,
          reverse,
          params,
        ),
        profile: surfaceProfileFromParams(params),
      };
    }
    case SURFACE_INSET_GENERATOR_ID: {
      const height = positive(numParam(params, "height", 1), 1);
      const reverse = boolParam(params, "reverse", false);
      const boundary = boundaryLoopFromParams(params);
      const insetDistance = Math.max(0, numParam(params, "insetDistance", 0));
      return {
        kind: "poly",
        outerLoop: surfaceInsetOffsetLoop(boundary, insetDistance),
        height,
        reverse,
        cutStartOffset: surfaceCutStartOffsetFromParams(
          height,
          reverse,
          params,
        ),
        profile: surfaceProfileFromParams(params),
      };
    }
  }
}

function bakeMeshScale(
  geometry: PrimitiveGeometry,
  scale: readonly [number, number, number],
): PrimitiveGeometry {
  if (
    scale[0] === IDENTITY_SCALE[0] &&
    scale[1] === IDENTITY_SCALE[1] &&
    scale[2] === IDENTITY_SCALE[2]
  ) {
    return geometry;
  }
  const vertices = new Float32Array(geometry.vertices);
  for (let i = 0; i < vertices.length; i += 3) {
    vertices[i] *= scale[0];
    vertices[i + 1] *= scale[1];
    vertices[i + 2] *= scale[2];
  }
  return {
    ...geometry,
    vertices,
  };
}

function surfaceCutOffsetCompensationShift(spec: SurfaceCutterSpec): number {
  if (spec.kind === "poly") return 0;
  if (!spec.reverse) return 0;
  if (normalizeSurfaceProfile(spec.profile).kind !== "prism") return 0;
  return (spec.cutStartOffset - surfaceCutStartOffset(spec.height)) / 2;
}

function translateVerticesZ(
  baseVertices: Float32Array,
  offset: number,
): Float32Array {
  const next = new Float32Array(baseVertices);
  for (let i = 2; i < next.length; i += 3) next[i] += offset;
  return next;
}

function outerLoopFromParams(
  params: GeneratorParams,
): readonly [number, number][] {
  const points: [number, number][] = [];
  for (const item of listParam(params, "outerLoop")) {
    points.push([numParam(item.params, "x", 0), numParam(item.params, "y", 0)]);
  }
  return points.length >= 3
    ? points
    : [
        [-0.5, -0.5],
        [0.5, -0.5],
        [0.5, 0.5],
        [-0.5, 0.5],
      ];
}

function boundaryLoopFromParams(
  params: GeneratorParams,
): readonly [number, number][] {
  const points: [number, number][] = [];
  for (const item of listParam(params, "boundaryLoop")) {
    points.push([numParam(item.params, "x", 0), numParam(item.params, "y", 0)]);
  }
  return points.length >= 3
    ? points
    : [
        [-0.5, -0.5],
        [0.5, -0.5],
        [0.5, 0.5],
        [-0.5, 0.5],
      ];
}

function surfaceInsetDistanceRange(params: GeneratorParams): {
  default: number;
  min: number;
  max: number;
  step: number;
} {
  const boundary = boundaryLoopFromParams(params);
  const minorExtent = polyMinorExtentFor(boundary);
  if (!(minorExtent > 0)) {
    return { default: 0.1, min: 0, max: 10, step: 0.005 };
  }
  // Geometric ceiling — half the minor extent collapses the inset; pull back
  // a hair so the offset stays valid against rounding.
  const max = Math.max(0, minorExtent * 0.5 - 1e-4);
  return {
    default: Math.min(max, minorExtent * 0.1),
    min: 0,
    max,
    step: Math.max(1e-6, minorExtent * 0.005),
  };
}

function polyMinorExtentFor(loop: readonly (readonly [number, number])[]): number {
  if (loop.length < 3) return 0;
  let xmin = Infinity;
  let xmax = -Infinity;
  let ymin = Infinity;
  let ymax = -Infinity;
  for (const [x, y] of loop) {
    if (x < xmin) xmin = x;
    if (x > xmax) xmax = x;
    if (y < ymin) ymin = y;
    if (y > ymax) ymax = y;
  }
  return Math.min(xmax - xmin, ymax - ymin);
}

/**
 * Offset a Surface Inset boundary loop inward by `insetDistance`, falling
 * back to the original boundary when the offset collapses below 3 verts.
 * Single source of truth shared between confirm-time profile resolution and
 * runtime spec construction (`surfaceSpecFromParams`) so the loop the user
 * sees in the gizmo matches both the persisted profile and the cutter mesh.
 */
export function surfaceInsetOffsetLoop(
  boundary: readonly (readonly [number, number])[],
  insetDistance: number,
): readonly (readonly [number, number])[] {
  const distance = Math.max(0, insetDistance);
  const result = offsetSimplePolygonInward(boundary, distance);
  return result.loop.length >= 3
    ? (result.loop as readonly (readonly [number, number])[])
    : boundary;
}

/**
 * Build SurfaceInsetParams from a confirm-time draft. Boundary is the
 * picked face's simplified loop in basis-UV (centered at the cap centroid),
 * and the offset loop is recomputed each build via `surfaceSpecFromParams`.
 */
export function surfaceInsetParamsFromDraft(args: {
  boundaryLoop: readonly (readonly [number, number])[];
  insetDistance: number;
  height: number;
  reverse: boolean;
  cutStartOffset: number;
  profile?: SurfaceCutterProfile;
}): SurfaceInsetParams {
  const fakeSpecForDefaults: SurfaceCutterSpec = {
    kind: "poly",
    outerLoop: args.boundaryLoop,
    height: positive(args.height, 1),
    reverse: args.reverse,
    cutStartOffset: args.cutStartOffset,
    profile: args.profile,
  };
  return {
    ...(SURFACE_INSET_GENERATOR.defaultParams as SurfaceInsetParams),
    height: positive(args.height, 1),
    reverse: args.reverse,
    insetDistance: Math.max(0, args.insetDistance),
    boundaryLoop: args.boundaryLoop.map(([x, y], index) =>
      pointItem(`p${index}`, x, y),
    ),
    cutStartOffset: surfaceCutStartOffsetFromSpec(fakeSpecForDefaults),
    ...surfaceProfileParamsFromSpec(args.profile),
    hollowThickness: defaultSurfaceHollowThickness(fakeSpecForDefaults),
  };
}

function surfaceProfileParamsFromSpec(
  profile: SurfaceCutterProfile | null | undefined,
): Pick<
  SurfaceGeneratorParams,
  "profile" | "rampAxisX" | "rampAxisY" | "rampBase" | "rampLead"
> {
  const normalized = normalizeSurfaceProfile(profile);
  if (normalized.kind !== "linearRamp") {
    return {
      profile: "prism",
      rampAxisX: 1,
      rampAxisY: 0,
      rampBase: 0,
      rampLead: 1,
    };
  }
  return {
    profile: "linearRamp",
    rampAxisX: normalized.axis[0],
    rampAxisY: normalized.axis[1],
    rampBase: normalized.base,
    rampLead: normalized.lead,
  };
}

function surfaceProfileFromParams(
  params: GeneratorParams,
): SurfaceCutterProfile {
  if (enumParam(params, "profile", "prism") !== "linearRamp") {
    return { kind: "prism" };
  }
  return normalizeSurfaceProfile({
    kind: "linearRamp",
    axis: [numParam(params, "rampAxisX", 1), numParam(params, "rampAxisY", 0)],
    base: numParam(params, "rampBase", 0),
    lead: numParam(params, "rampLead", 1),
  });
}

function hiddenNumber(id: string, fallback: number): GeneratorParamSpec {
  return {
    id,
    label: id,
    type: "number",
    default: fallback,
    hidden: true,
  };
}

function hiddenBoolean(id: string, fallback: boolean): GeneratorParamSpec {
  return {
    id,
    label: id,
    type: "boolean",
    default: fallback,
    hidden: true,
  };
}

function hiddenEnum(
  id: string,
  fallback: string,
  options: readonly { value: string; label: string }[],
): GeneratorParamSpec {
  return {
    id,
    label: id,
    type: "enum",
    default: fallback,
    options,
    hidden: true,
  };
}

function pointItem(id: string, x: number, y: number): GeneratorListItem {
  return {
    id,
    itemType: POLY_POINT_ITEM.id,
    params: { x, y },
  };
}

function positive(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}
